import classNames from 'classnames'
import moment from 'moment'
import { FC, useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { Skeleton } from 'src/components/Skeleton'
import { useGetAiListeningSiteAuditQuery } from 'src/reactQuery/useGetAiListeningSiteAuditQuery'
import { useRunAiListeningSiteAuditMutation } from 'src/reactQuery/useRunAiListeningSiteAuditMutation'
import { SiteAuditStatus } from 'src/services/aiListeningApi/brandProfile.types'
import styles from './siteAudit.module.scss'
import { Pill, PillTone } from '../Pill'

interface SiteAuditCardProps {
  brandName: string
  profileId: string
  subjectId: string
}

const STATUS: Record<SiteAuditStatus, { label: string; tone: PillTone }> = {
  fail: { label: 'Fix', tone: 'red' },
  pass: { label: 'OK', tone: 'green' },
  warn: { label: 'Improve', tone: 'amber' },
}

const scoreTone = (score: number) =>
  score >= 80
    ? styles.scoreGood
    : score >= 50
      ? styles.scoreFair
      : styles.scorePoor

// AI Site Audit: a technical check of how ready the brand's (or a
// competitor's) website is for AI assistants to read and cite. A profile
// with a website and no audit yet is audited on first view.
export const SiteAuditCard: FC<SiteAuditCardProps> = ({
  brandName,
  profileId,
  subjectId,
}) => {
  const { data, isLoading } = useGetAiListeningSiteAuditQuery(
    profileId,
    subjectId
  )
  const runMutation = useRunAiListeningSiteAuditMutation({
    onError: (error) =>
      toast.error(error.response?.data?.error || 'Could not audit the site'),
  })
  const autoRan = useRef<string | null>(null)

  const audit = data?.audit
  const hasWebsite = !!data?.website

  useEffect(() => {
    if (data && !data.audit && data.website && autoRan.current !== subjectId) {
      autoRan.current = subjectId
      runMutation.mutate({ profileId, subjectId })
    }
    // Not keyed on runMutation: its identity changes on every render.
  }, [data, profileId, subjectId])

  const isRunning = runMutation.isPending
  const checks = audit
    ? [...audit.checks].sort(
        (a, b) =>
          ['fail', 'warn', 'pass'].indexOf(a.status) -
            ['fail', 'warn', 'pass'].indexOf(b.status) || b.weight - a.weight
      )
    : []

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <div>
          <h2 className={styles.title}>AI Site Audit</h2>
          <p className={styles.subtitle}>
            How ready {brandName}&apos;s website is for ChatGPT, Gemini, Claude
            and Perplexity to read, cite and recommend.
            {audit &&
              ` ${audit.url} · checked ${moment(audit.checkedAt).format(
                'DD/MM/YYYY HH:mm'
              )}`}
          </p>
        </div>
        {hasWebsite && (
          <button
            className={styles.runButton}
            disabled={isLoading || isRunning}
            onClick={() => runMutation.mutate({ profileId, subjectId })}
            type="button"
          >
            {isRunning ? 'Checking…' : audit ? 'Re-run audit' : 'Run audit'}
          </button>
        )}
      </div>

      {(isLoading || (isRunning && !audit)) && (
        <Skeleton height={160} radius={12} width="100%" />
      )}

      {!isLoading && !hasWebsite && (
        <p className={styles.empty}>
          Add {brandName}&apos;s website to the profile to audit it.
        </p>
      )}

      {audit && (
        <div className={styles.body}>
          <div className={classNames(styles.score, scoreTone(audit.score))}>
            <span className={styles.scoreValue}>{audit.score}</span>
            <span className={styles.scoreLabel}>/ 100</span>
          </div>
          <ul className={styles.list}>
            {checks.map((check) => (
              <li className={styles.item} key={check.key}>
                <Pill size="sm" tone={STATUS[check.status].tone}>
                  {STATUS[check.status].label}
                </Pill>
                <div className={styles.itemText}>
                  <span className={styles.itemLabel}>{check.label}</span>
                  <span className={styles.itemDetail}>{check.detail}</span>
                  {check.status !== 'pass' && check.fix && (
                    <span className={styles.itemFix}>{check.fix}</span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
